import Link from 'next/link';
import { FC, forwardRef, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { gsap } from 'react-scale/lib/gsap';

import { CTA } from 'react-scale/components/pages/zeitgeist/primitives/cta';
import { Container } from 'react-scale/components/pages/zeitgeist/layout/container';
import s from './header.module.scss';
import { Drawer } from '../drawer';
import { useMedia } from 'react-use';
import { NextSeo } from 'next-seo';

export const Header: FC<{ form?: 'long' | 'short' }> = ({ form = 'long' }) => {
  const svgRef = useRef<SVGSVGElement>();
  const isDesktop = useMedia('(min-width: 1024px)');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!svgRef.current || !isDesktop) return;

    const fillGradient = svgRef.current.querySelector('#paint0_linear_229_9437');

    const handleMouseEnter = () => {
      gsap.to(fillGradient, {
        duration: 0.3,
        attr: { gradientTransform: 'translate(-105, 0)' },
        ease: 'linear'
      });
    };
    const handleMouseLeave = () => {
      gsap.to(fillGradient, {
        duration: 0.3,
        attr: { gradientTransform: 'translate(0, 0)' },
        ease: 'linear'
      });
    };

    svgRef.current.addEventListener('mouseenter', handleMouseEnter);
    svgRef.current.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (svgRef.current) {
        svgRef.current.removeEventListener('mouseenter', handleMouseEnter);
        svgRef.current.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [isDesktop]);

  useEffect(() => {
    const entrance = () => setLoaded(true);

    if (document.readyState === 'complete') {
      setTimeout(entrance, 500);
    } else {
      window.addEventListener('load', entrance);
    }

    return () => {
      window.removeEventListener('load', entrance);
    };
  }, []);

  return (
    <>
      <NextSeo
        title="AI Readiness Report 2023 | Scale AI"
        description="We surveyed 1,600 executives and ML practitioners to look past the hype and help you separate the signal from the noise on what it really takes to adopt Generative AI to help you solve your most important business challenges."
        canonical="https://scale.com/ai-readiness-report"
        openGraph={{
          images: [
            {
              url: 'https://scale.com/static/zeitgeist/2023/OG.jpg',
              width: 1200,
              height: 642,
              alt: 'Og Image Alt',
              type: 'image/jpeg'
            }
          ]
        }}
      />
      <Container>
        <header className={s.header}>
          <Link href="/" className={cn(s.link, loaded && s['link--loaded'])}>
            <ZeitgeistIcon ref={svgRef} />
          </Link>

          <Drawer
            form={form}
            trigger={
              <CTA className={cn(s.cta, loaded && s['cta--loaded'])} arrow="bottom">
                Download the full report
              </CTA>
            }
          />
        </header>
      </Container>
    </>
  );
};

const ZeitgeistIcon = forwardRef<SVGSVGElement, {}>(({}, ref) => {
  return (
    <svg
      ref={ref}
      className={s.logo}
      viewBox="0 0 93 38"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask id="path-1-inside-1_229_9437" fill="#fff">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M93 37.806H55.8V.606H93v37.2Zm-30.4-3.988h26.412V7.406L62.6 33.818ZM86.2 4.594H59.788v26.412L86.2 4.594ZM18.6 19.206l18.6 18.6V.606H0l18.6 18.6Zm-9.3 9.3 9.3 9.3v-18.6H0l9.3 9.3Zm0 0H0l9.3 9.3v-9.3Z"
        />
      </mask>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M93 37.806H55.8V.606H93v37.2Zm-30.4-3.988h26.412V7.406L62.6 33.818ZM86.2 4.594H59.788v26.412L86.2 4.594ZM18.6 19.206l18.6 18.6V.606H0l18.6 18.6Zm-9.3 9.3 9.3 9.3v-18.6H0l9.3 9.3Zm0 0H0l9.3 9.3v-9.3Z"
        fill="url(#paint0_linear_229_9437)"
      />
      <path
        d="M58.62 37.806v-.241.241Zm34.38 0v.24h.24v-.24H93Zm-37.2 0h-.24v.24h.24v-.24Zm0-2.82h-.24.24Zm0-34.38V.365h-.24v.241h.24Zm34.38 0V.365v.241Zm2.82 0h.24V.365H93v.241Zm-3.988 33.212v.241h.241v-.24h-.24Zm-26.412 0-.17-.17-.411.411h.581v-.24ZM89.012 7.406h.241v-.582l-.411.412.17.17ZM59.788 4.594v-.241h-.241v.24h.24Zm26.412 0 .17.17.411-.411H86.2v.24ZM59.788 31.006h-.241v.581l.411-.41-.17-.171Zm-22.588 6.8-.17.17.41.412v-.582h-.24Zm0-37.2h.24v-.24h-.24v.24ZM0 .606v-.24h-.582l.412.41.17-.17Zm18.6 37.2-.17.17.41.412v-.582h-.24ZM0 19.206v-.24h-.582l.412.41.17-.17Zm0 9.3v-.24h-.582l.412.41.17-.17Zm9.3 9.3-.17.17.41.412v-.582H9.3Zm49.32.24H93v-.481H58.62v.482Zm-2.82 0h2.82v-.481H55.8v.482Zm-.24-3.06v2.82h.48v-2.82h-.48Zm0-34.38v34.38h.48V.606h-.48ZM90.18.365H55.8v.482h34.38V.365Zm2.82 0h-2.82v.482H93V.365Zm.24 3.06V.607h-.48v2.82h.48Zm0 34.381V3.426h-.48v34.38h.48Zm-4.228-4.229H62.6v.482h26.412v-.482Zm-.24-26.17v26.411h.481V7.406h-.482ZM62.77 33.988 89.183 7.576l-.341-.34L62.43 33.648l.34.34ZM59.788 4.835H86.2v-.482H59.788v.482Zm.24 26.17V4.595h-.481v26.412h.482ZM86.03 4.424 59.617 30.835l.341.341L86.37 4.764l-.34-.34ZM37.37 37.636l-18.6-18.6-.34.34 18.6 18.6.34-.34ZM36.96.606v37.2h.48V.606h-.48ZM0 .847h37.2V.365H0v.482Zm18.77 18.189L.17.436l-.34.34 18.6 18.6.34-.34Zm0 18.6-9.3-9.3-.34.34 9.3 9.3.34-.34Zm-.41-18.43v18.6h.48v-18.6h-.48ZM0 19.447h18.6v-.482H0v.482Zm9.47 8.889-9.3-9.3-.34.34 9.3 9.3.34-.34ZM0 28.747h9.3v-.482H0v.482Zm9.47 8.889-9.3-9.3-.34.34 9.3 9.3.34-.34Zm-.41-9.13v9.3h.48v-9.3h-.48Z"
        fill="url(#paint1_linear_229_9437)"
        mask="url(#path-1-inside-1_229_9437)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_229_9437"
          x1="21.335"
          y1="-3.765"
          x2="33.255"
          y2="52.001"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fff" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_229_9437"
          x1="19"
          y1="-14.5"
          x2="105"
          y2="75.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fff" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
});
